import React from "react";
import {
    Box,
    useColorModeValue,
    Grid,
    FormControl,
    FormLabel,
    GridItem,
    Input,
    Select,
    Text,
    FormErrorMessage,
} from "@chakra-ui/react";
import Timer from "./Timer";
import { useTimerTracker } from "../hooks/useTimeTracker";
import TimerSelector from "./TimerSelector";

type TimerBoxProps = Pick<useTimerTracker.type, 'state' | 'loadTasks' | 'switchType' | 'confirmTimer' | 'changeInput' | 'changeSelects' | 'displayInputError'>;

const TimerBox: React.FC<TimerBoxProps> = ({ state, loadTasks, switchType, confirmTimer, displayInputError, changeInput, changeSelects }) => {

    const textColor = useColorModeValue("navy.700", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");

    return (
        <>
            <Box
                w={"100%"}
                p={4}
                mb={4}
                backgroundColor={"gray.100"}
                borderRadius={"lg"}
            >
                <Grid
                    w={"100%"}
                    templateColumns='repeat(7, 1fr)'
                    templateRows='repeat(1, 1fr)'
                    gap={4}
                >
                    <GridItem colSpan={2} rowSpan={1} >
                        <FormControl>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                            >
                                Descrição
                            </FormLabel>
                            <Input
                                isRequired={true}
                                fontSize="sm"
                                ms={{ base: "0px", md: "0px" }}
                                type="text"
                                placeholder="Preencha uma descrição"
                                fontWeight="500"
                                value={state.timer?.description || ''}
                                size="md"
                                onChange={(e) => changeInput({ type: "description", payload: e.target.value })}
                                isDisabled={state.loading}
                            />
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Projetos
                            </FormLabel>
                            <Select
                                size="md"
                                value={state.projectSelected?.id || ''}
                                onChange={(e) => {
                                    changeSelects({ type: "projectSelected", payload: state.projects.find(item => item.id === Number(e.target.value)) })
                                    loadTasks(Number(e.target.value));
                                }}
                                placeholder={'Selecione'}
                                isDisabled={state.loading}
                            >
                                {state.projects?.map((item) => (
                                    <option key={item.id} value={item.id}>{item.descricao}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </GridItem>

                    <GridItem colSpan={1} rowSpan={1} >
                        <FormControl>
                            <FormLabel
                                display="flex"
                                ms="4px"
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                                mb="8px"
                            >
                                Tarefa
                            </FormLabel>
                            <Select
                                size="md"
                                value={state.taskSelected?.id || ''}
                                onChange={(e) =>
                                    changeSelects({ type: "taskSelected", payload: state.tasksList.find(item => item.id === e.target.value) })
                                }
                                placeholder={'Selecione'}
                                isDisabled={state.loading}
                            >
                                {state.tasksList?.map((item) => (
                                    <option key={item.id} value={item.id}>{item.code} - {item.titulo}</option>
                                ))}
                            </Select>
                        </FormControl>
                    </GridItem>

                    {state.inputType === 'auto' ? (
                        <GridItem colSpan={3} rowSpan={1} >
                            <Box
                                display="flex"
                                h="100%"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={4}
                            >
                                <Timer time={state.timer?.initialMoment || null} />

                                <TimerSelector
                                    state={state}
                                    switchType={switchType}
                                    confirmTimer={confirmTimer}
                                />
                            </Box>
                        </GridItem>
                    )
                        : (
                            <>
                                <GridItem colSpan={1} rowSpan={1} borderLeft={'1px'} borderLeftColor={'gray.300'} pl={4}>
                                    <FormControl isInvalid={displayInputError("initialMoment").error}>
                                        <FormLabel
                                            display="flex"
                                            ms="4px"
                                            fontSize="sm"
                                            fontWeight="500"
                                            color={textColor}
                                        >
                                            Momento Inicio<Text color={brandStars}>*</Text>
                                        </FormLabel>
                                        <Input
                                            isRequired={true}
                                            fontSize="sm"
                                            ms={{ base: "0px", md: "0px" }}
                                            type="datetime-local"
                                            placeholder="Preencha uma descrição"
                                            fontWeight="500"
                                            size="md"
                                            value={state.timer?.initialMoment ? new Date(state.timer.initialMoment).toISOString().slice(0, 16) : ""}
                                            onChange={(e) => changeInput({ type: "initialMoment", payload: new Date(e.target.value) })}
                                            isDisabled={state.loading}
                                        />

                                        {displayInputError("initialMoment").error && (
                                            <FormErrorMessage>{displayInputError("initialMoment").errorMessage}</FormErrorMessage>
                                        )}
                                    </FormControl>
                                </GridItem>

                                <GridItem colSpan={1} rowSpan={1} >
                                    <FormControl isInvalid={displayInputError("finalMoment").error}>
                                        <FormLabel
                                            display="flex"
                                            ms="4px"
                                            fontSize="sm"
                                            fontWeight="500"
                                            color={textColor}
                                        >
                                            Momento Final<Text color={brandStars}>*</Text>
                                        </FormLabel>
                                        <Input
                                            isRequired={true}
                                            fontSize="sm"
                                            ms={{ base: "0px", md: "0px" }}
                                            type="datetime-local"
                                            placeholder="Preencha uma descrição"
                                            fontWeight="500"
                                            size="md"
                                            value={state.timer?.finalMoment ? new Date(state.timer.finalMoment).toISOString().slice(0, 16) : ""}
                                            onChange={(e) => changeInput({ type: "finalMoment", payload: new Date(e.target.value) })}
                                            isDisabled={state.loading}
                                        />

                                        {displayInputError("finalMoment").error && (
                                            <FormErrorMessage>{displayInputError("finalMoment").errorMessage}</FormErrorMessage>
                                        )}
                                    </FormControl>
                                </GridItem>

                                <GridItem colSpan={1} rowSpan={1} >
                                    <Box
                                        display="flex"
                                        h="100%"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                        gap={4}
                                    >
                                        <TimerSelector
                                            state={state}
                                            switchType={switchType}
                                            confirmTimer={confirmTimer}
                                        />
                                    </Box>
                                </GridItem>
                            </>
                        )}
                </Grid>
            </Box >
        </>
    );
}

export default TimerBox;